/**
 * §A.6 · Los catálogos globales.
 *
 * ## Todo lo de aquí lo comparten los doscientos clubes
 *
 * Es la única cosa que hay que tener en la cabeza al abrir esta sección, y por eso
 * cada uno de los tres tipos trae **una cifra que dice si se puede tocar**:
 * `academies` en una disciplina, `used_by` en una plantilla de grados, `students` en
 * un cinturón y `signatures` en un consentimiento.
 *
 * Esas cifras convierten «no se puede borrar» en «lo llevan catorce alumnos», que es
 * la única forma de que alguien entienda por qué no y no lo intente por otro lado.
 *
 * ## Cambiar el texto de un consentimiento **no modifica lo ya firmado**
 *
 * El aviso de H-8, literal: «un `PATCH` que reescriba el texto dejaría 3.000 firmas
 * apuntando a un texto que nadie leyó». Cada versión se guarda entera y las firmas
 * siguen apuntando a la suya.
 *
 * Y la consecuencia menos evidente: `outdated_signatures` **no es un error**. Son
 * familias que firmaron el texto que había, y ese texto sigue existiendo. Es la cifra
 * que dice a cuántas hay que volver a pedírselo.
 *
 * ## `used_by` cuenta las dos formas de seguir una plantilla
 *
 * Adoptada y clonada con el mismo código: «contar solo la primera daría un cero
 * tranquilizador sobre un sistema que en realidad siguen ochenta clubes por copia».
 */

export interface DisciplinaGlobal {
  readonly id: string;
  /** No se edita: enlaza la disciplina con sus sistemas de grados y con el seed. */
  readonly code: string;
  readonly name: string;
  readonly has_weight_classes: boolean;
  readonly sort_order: number;
  /** Cuántos clubes la enseñan. **Es lo que dice si se puede tocar.** */
  readonly academies: number;
  readonly schemes: number;
}

export interface CinturonGlobal {
  readonly id: string;
  readonly name: string;
  readonly short_name: string | null;
  /** El color **del cinturón**, jamás la marca del club. */
  readonly color_hex: string;
  readonly color_hex_2: string | null;
  readonly sort_order: number;
  readonly max_stripes: number;
  readonly is_dan: boolean;
  readonly min_months_since_last: number | null;
  readonly min_attendance_rate: number | null;
  readonly min_classes: number | null;
  readonly min_age: number | null;
  /** Solo para menores: no se da desde esta edad (el Pum, el cinto negro juvenil). */
  readonly max_age: number | null;
  /** Cuántos alumnos lo llevan hoy. Dice si tocarlo es una corrección o una bomba. */
  readonly students: number;
}

export interface PlantillaDeGrados {
  readonly id: string;
  readonly discipline_id: string;
  readonly discipline_name: string;
  readonly code: string;
  readonly name: string;
  readonly federation: string | null;
  readonly rank_count: number;
  readonly ranks: readonly CinturonGlobal[];
  /** Clubes que la siguen, **adoptada o clonada**. Ver la cabecera. */
  readonly used_by: number;
  readonly created_at: Date | string;
}

export interface VersionDeConsentimiento {
  readonly version: number;
  readonly label: string;
  readonly published_at: Date | string;
  readonly signatures: number;
}

export interface TipoDeConsentimiento {
  /** El identificador **es el código**, no un uuid. */
  readonly code: string;
  readonly label: string;
  readonly description: string | null;
  readonly is_required: boolean;
  readonly sort_order: number;
  readonly current_version: number;
  readonly signatures: number;
  /** Firmas vivas de una versión anterior. **No es un error.** Ver la cabecera. */
  readonly outdated_signatures: number;
  readonly versions: readonly VersionDeConsentimiento[];
}

/**
 * Qué campos de un consentimiento **publican una versión nueva** al cambiar.
 *
 * `label`, `description` e `is_required` sí; `sort_order` no, porque «el orden en la
 * pantalla no es el documento, y publicar una versión por reordenar una lista pondría
 * a tres mil familias a firmar otra vez lo mismo».
 *
 * Está aquí y no en la pantalla porque es la regla que hay que **avisar antes** de
 * guardar, y una regla que se avisa tiene que poder comprobarse en una prueba.
 */
export function publicaVersion(
  antes: TipoDeConsentimiento,
  despues: { label: string; description: string | null; is_required: boolean }
): boolean {
  return (
    antes.label !== despues.label ||
    (antes.description ?? '') !== (despues.description ?? '') ||
    antes.is_required !== despues.is_required
  );
}

/**
 * ¿Se puede tocar esto sin hacer daño?
 *
 * Devuelve la frase, no un booleano: lo que hace falta en pantalla es explicarlo. Un
 * botón gris sin explicación se rodea por otro lado, y en un catálogo global «por
 * otro lado» es un `UPDATE` a mano en producción.
 */
export function avisoDeUso(
  cuantos: number,
  queSon: string,
  quePasa: string
): string | null {
  if (cuantos === 0) return null;
  return `Lo ${cuantos === 1 ? 'usa' : 'usan'} ${cuantos} ${queSon}: ${quePasa}`;
}
